import React, { useEffect, useState } from "react";
import { Save, RefreshCw, Check, AlertCircle, Wallet, Building2, Send, Settings as SettingsIcon, ShieldCheck } from "lucide-react";
import api from "../../lib/api";
import AdminLayout from "./AdminLayout";
import { useAuth } from "../../context/AuthContext";

const Field = ({ label, value, onChange, mono = false, type = "text", hint, placeholder }) => (
  <div>
    <div className="text-[11px] text-white/55 mb-1.5">{label}</div>
    <input
      type={type}
      value={value ?? ""}
      onChange={(e) => onChange(type === "number" ? Number(e.target.value) : e.target.value)}
      placeholder={placeholder}
      className={`w-full h-11 px-3 rounded-lg bg-white/[0.02] border border-white/8 text-[13px] text-white outline-none focus:border-[#ff7a1a] placeholder:text-white/30 ${mono ? "font-mono" : ""}`}
    />
    {hint && <div className="text-[10px] text-white/40 mt-1">{hint}</div>}
  </div>
);

const Section = ({ icon: Icon, title, desc, children }) => (
  <div className="bb-card rounded-2xl p-5 lg:p-6">
    <div className="flex items-center gap-2 mb-1">
      <Icon size={15} className="text-[#ffae6a]" />
      <div className="font-display font-semibold text-[15px]">{title}</div>
    </div>
    {desc && <p className="text-[12px] text-white/45 mb-5">{desc}</p>}
    <div className="space-y-4">{children}</div>
  </div>
);

const AdminSettings = () => {
  const { user, setUser } = useAuth();
  const [data, setData] = useState(null);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [err, setErr] = useState(null);

  // Admin access-key rotation state
  const [newKey, setNewKey] = useState("");
  const [credBusy, setCredBusy] = useState(false);
  const [credMsg, setCredMsg] = useState(null);
  const [credErr, setCredErr] = useState(null);

  const load = async () => {
    try {
      const { data } = await api.get("/admin/settings");
      setData(data);
    } catch { /* noop */ }
  };
  useEffect(() => { load(); }, []);

  const update = (k, v) => setData((d) => ({ ...d, [k]: v }));

  const changeKey = async () => {
    setCredErr(null);
    setCredMsg(null);
    if (!newKey || newKey.trim().length < 8) return setCredErr("Access key must be at least 8 characters.");
    setCredBusy(true);
    try {
      await api.post("/admin/set-access-key", { new_key: newKey.trim() });
      setCredMsg("Access key updated. Use the new key next time you sign in.");
      setNewKey("");
    } catch (e) {
      setCredErr(e?.response?.data?.detail || "Could not update access key.");
    } finally {
      setCredBusy(false);
    }
  };

  const save = async () => {
    setSaving(true);
    setErr(null);
    try {
      const { data: updated } = await api.put("/admin/settings", data);
      setData(updated);
      setSaved(true);
      setTimeout(() => setSaved(false), 2200);
    } catch (e) {
      setErr(e?.response?.data?.detail || "Failed to save.");
    } finally {
      setSaving(false);
    }
  };

  if (!data) {
    return (
      <AdminLayout title="Settings" subtitle="Platform configuration">
        <div className="py-16 text-center text-white/40 text-[13px]">Loading…</div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout title="Settings" subtitle="Platform configuration">
      <div className="grid lg:grid-cols-2 gap-4 lg:gap-5">
        <Section icon={Wallet} title="Crypto deposit addresses" desc="Wallet addresses displayed to customers on deposit screens. Rotate anytime — no redeploy needed.">
          <Field label="USDT (TRC20) address" value={data.usdt_address} onChange={(v) => update("usdt_address", v)} mono />
          <Field label="TRX (Tron) address" value={data.trx_address} onChange={(v) => update("trx_address", v)} mono />
          <Field label="BTC address" value={data.btc_address} onChange={(v) => update("btc_address", v)} mono />
        </Section>

        <Section icon={Building2} title="Bank transfer (Naira)" desc="Bank account shown for Naira deposits. Rate controls Naira ↔ USD conversion shown to customers.">
          <Field label="Beneficiary" value={data.bank_beneficiary} onChange={(v) => update("bank_beneficiary", v)} />
          <Field label="Account number" value={data.bank_account} onChange={(v) => update("bank_account", v)} mono />
          <Field label="Bank name" value={data.bank_name} onChange={(v) => update("bank_name", v)} />
          <Field
            label="USDT → NGN rate"
            value={data.usdt_ngn_rate}
            onChange={(v) => update("usdt_ngn_rate", v)}
            type="number"
            hint="Naira per 1 USDT. Updates the conversion shown on customer deposit screens instantly."
          />
        </Section>

        <Section icon={Send} title="Support" desc="Telegram contact link surfaced across the customer dashboard.">
          <Field label="Telegram URL" value={data.telegram_url} onChange={(v) => update("telegram_url", v)} placeholder="https://t.me/..." />
          <Field label="Telegram handle" value={data.telegram_handle} onChange={(v) => update("telegram_handle", v)} placeholder="@yourhandle" />
        </Section>

        <Section icon={SettingsIcon} title="Limits & mode" desc="Minimums and platform-wide kill switch.">
          <div className="grid grid-cols-2 gap-3">
            <Field label="Min deposit ($)" value={data.min_deposit} onChange={(v) => update("min_deposit", v)} type="number" />
            <Field label="Min withdrawal ($)" value={data.min_withdrawal} onChange={(v) => update("min_withdrawal", v)} type="number" />
          </div>
          <label className="flex items-center justify-between gap-3 px-3 py-2.5 rounded-lg border border-white/8 bg-white/[0.02] cursor-pointer">
            <div>
              <div className="text-[13px] text-white font-medium">Maintenance mode</div>
              <div className="text-[11px] text-white/45">When on, customer dashboard shows a maintenance message.</div>
            </div>
            <button
              type="button"
              onClick={() => update("maintenance_mode", !data.maintenance_mode)}
              className={`relative w-10 h-6 rounded-full transition-colors ${data.maintenance_mode ? "bg-[#ff7a1a]" : "bg-white/15"}`}
            >
              <span className={`absolute top-0.5 w-5 h-5 rounded-full bg-white transition-transform ${data.maintenance_mode ? "translate-x-4" : "translate-x-0.5"}`}></span>
            </button>
          </label>
        </Section>

        <Section icon={ShieldCheck} title="Admin access key" desc="Rotate the secret key used to sign in to this console. Takes effect immediately — no redeploy needed. Store it somewhere safe; it's the only way in.">
          <Field label="New access key" value={newKey} onChange={setNewKey} mono hint="Minimum 8 characters. Anyone with this key can access the admin console." placeholder="Enter a strong new key" />
          {credErr && (
            <div className="flex items-center gap-2 text-[12px] text-rose-300" data-testid="cred-error">
              <AlertCircle size={14} /> {credErr}
            </div>
          )}
          {credMsg && (
            <div className="flex items-center gap-2 text-[12px] text-emerald-300" data-testid="cred-success">
              <Check size={14} /> {credMsg}
            </div>
          )}
          <button
            onClick={changeKey}
            disabled={credBusy}
            data-testid="change-key-btn"
            className="w-full h-10 rounded-lg text-white text-[13px] font-medium inline-flex items-center justify-center gap-2 disabled:opacity-60"
            style={{ background: "linear-gradient(135deg,#ff7a1a,#c2410c)" }}
          >
            <ShieldCheck size={14} /> {credBusy ? "Updating…" : "Update access key"}
          </button>
        </Section>
      </div>

      <div className="flex items-center justify-end gap-3 mt-6">
        {err && (
          <div className="flex items-center gap-2 text-[12px] text-rose-300">
            <AlertCircle size={14} /> {err}
          </div>
        )}
        <button onClick={load} className="px-4 h-10 rounded-lg border border-white/10 text-white/70 text-[13px] inline-flex items-center gap-2">
          <RefreshCw size={13} /> Reload
        </button>
        <button
          onClick={save}
          disabled={saving}
          className="px-5 h-10 rounded-lg text-white text-[13px] font-medium inline-flex items-center gap-2 disabled:opacity-60"
          style={{ background: "linear-gradient(135deg,#ff7a1a,#c2410c)" }}
        >
          {saved ? <Check size={14} /> : <Save size={14} />}
          {saving ? "Saving…" : saved ? "Saved" : "Save changes"}
        </button>
      </div>
    </AdminLayout>
  );
};

export default AdminSettings;
